// Resolving a document's merge-bound fields at send time.
//
// CONSUMER: shared (Workers send route + node-pool tests).
//
// Grant, 2026-08-22: documents need to merge in "field data from
// basically any field in the opportunity, processing, or other record."
//
// The values come from the SAME engine the automation emails use, so
// this module never fetches anything itself — it takes an already-built
// merge context and decides three things:
//
//   1. which fields still need a value (a binding with no override),
//   2. what each resolves to,
//   3. which ones came back EMPTY — because a contract that goes out
//      with a blank where a price should be is the accuracy failure the
//      OS's own rules forbid, and it must stop the send rather than
//      reach a client looking finished.
//
// Pure and dependency-free, the field-types.ts rule.

import { applyMergeFields, type MergeContext } from "../microsoft-graph/merge-fields";
import { addMonthsToContractDate } from "./envelope-date";
import { PREPARER_SIGNER_KEY } from "./field-types";

/** The day a package goes out, and the date six months after it. */
export const SEND_DATE_TOKEN = "envelope.date_sent";
export const SEND_DATE_PLUS_6_TOKEN = "envelope.date_sent_plus_six_months";

/**
 * THE END DATE FOLLOWS THE START DATE (Nate, 2026-10-21, on the Buyer-
 * Broker agreement: line 10 "can automatically adjust to 6 months from
 * the date on line 9, but should also be able to be overridden").
 *
 * When the sender types over the send-date box on this document, the
 * six-months box is worked out from THEIR date instead of today's. Typing
 * over the six-months box itself still wins (the ordinary override rule),
 * so the pair can each be adjusted independently.
 *
 * DECISION: scoped to one document's fields, not the package, because
 * "line 9" is a line on one agreement; another form's start date must
 * not move this form's end date.
 */
export function sixMonthsAfterTypedStart(fields: readonly BindableField[]): string | null {
  for (const f of fields) {
    if ((f.mergeToken ?? "").trim() !== SEND_DATE_TOKEN || !hasOverride(f)) continue;
    const later = addMonthsToContractDate(f.value ?? "", 6);
    if (later) return later;
  }
  return null;
}

export interface BindableField {
  /** The merge token this field is bound to, when it is bound. */
  mergeToken?: string | null;
  /** Which signer's box this is. A `contact.*` token reads THAT
   *  person's record — see isContactScopedToken. Optional because the
   *  pre-flight caller (unfilledBindings) asks a question that doesn't
   *  depend on whose box it is. */
  signerKey?: string;
  /** WHOSE BOX A LOCKED ONE IS (Baker, 2026-09-16).
   *
   *  A locked box belongs to the preparer, so `signerKey` above is the
   *  preparer's sentinel and names no person. That is right for the
   *  boilerplate a locked box usually holds, and wrong for the case
   *  Baker hit: a locked box carrying the BUYER'S NAME, which the
   *  signer must not be able to edit and which still has to come from
   *  a specific person.
   *
   *  The seat is how such a box names its person without owning them.
   *  `buyer_1` is a chair, not a name; who is sitting in it is decided
   *  per send by the casting. Null on every ordinary locked box and on
   *  every signer's own box, where `signerKey` already answers it. */
  roleKey?: string | null;
  /** A static prefill, OR — on a bound field — the advisor's override
   *  for this one send. Set means "don't resolve, use this". */
  value?: string | null;
  /** For the plain-English message naming what still needs a value. */
  fieldType: string;
  pageNumber: number;
}

export interface ResolvedBinding {
  /** Index into the input array, so the caller can write the value back
   *  onto its own field objects without this module knowing their shape. */
  index: number;
  token: string;
  value: string;
}

export interface ResolveBindingsResult<T extends BindableField> {
  /** The fields with every resolvable binding filled in. */
  fields: T[];
  resolved: ResolvedBinding[];
  /** Bindings that came back empty. A non-empty list must BLOCK the
   *  send — see the module header. */
  missing: { index: number; token: string; pageNumber: number }[];
}

/** Every token a set of fields is bound to — what the caller passes to
 *  buildMergeContext so it fetches only what is actually needed. */
export function referencedBindingTokens(fields: BindableField[]): Set<string> {
  const out = new Set<string>();
  for (const f of fields) {
    const token = (f.mergeToken ?? "").trim();
    // An override means this field is NOT resolved on this send, so its
    // token doesn't need fetching.
    if (token && !hasOverride(f)) out.add(token);
  }
  return out;
}

function hasOverride(f: BindableField): boolean {
  return typeof f.value === "string" && f.value.length > 0;
}

/**
 * Does this token mean "the person whose box this is", rather than
 * something the whole document shares?
 *
 * Baker, 2026-09-14: "it was pulling in Tirsa's information into the
 * Brian field." He had put a name box on the Borrower line for each of
 * two signers, correctly assigned, and both printed the first signer's
 * name.
 *
 * The cause is that `contact.*` is the ONLY family of tokens that reads
 * a person rather than the record — in the context builder it is the
 * single thing `recipientContactId` drives — and the send used to build
 * one context for the whole envelope pinned to `signers[0]`. Everything
 * else (the loan number, the property address, the household) genuinely
 * is the same value in every box, and must keep being worked out once.
 *
 * So the split is exactly this prefix, and it is deliberately a prefix
 * test rather than a list: a `contact.` token added to the registry
 * later is per-signer on the day it is added, with nothing to remember.
 */
export function isContactScopedToken(token: string): boolean {
  return token.trim().startsWith("contact.");
}

/**
 * Does this token print THE SIGNER'S OWN NAME as it is on this send,
 * rather than anything read from a record?
 *
 * Baker, 2026-10-03, mapping the Realty Policy and Procedure Manual
 * statement every Realty team member signs each year: "I would love for
 * it to auto populate the name when I actually go to send the document
 * out." The only name field there was `contact.legal_full_name`, and a
 * team member is not a contact. On a Productions send they sign as a
 * named signer (a name and an email), so that field could only ever be
 * refused at send, every year, for every team member.
 *
 * `signer.*` reads the name the envelope is actually addressed to, for
 * whoever's box it is. Every signer has one (the send refuses a signer
 * without a name), so it fills for a team member, a new hire not in the
 * OS yet, a co-op agent and a client alike. It is a different fact from
 * `contact.legal_full_name`, which stays the right choice for a client's
 * legal name: one is "what we called them on this send", the other is
 * "what their contact record says their legal name is".
 */
export function isSignerScopedToken(token: string): boolean {
  return token.trim().startsWith("signer.");
}

/**
 * Does this token mean "the person whose box this is" in either sense:
 * a fact on their contact record, or their name on this send? Both are
 * answered per box by the same "whose box is this" rule
 * (bindingPersonSignerKey), so a locked box carrying either needs a
 * seat, and both preview per signer rather than once for the page.
 */
export function isPersonScopedToken(token: string): boolean {
  return isContactScopedToken(token) || isSignerScopedToken(token);
}

/**
 * WHOSE CONTACT RECORD A FIELD READS.
 *
 * Almost always the signer whose box it is. The exception is the one
 * Baker found on 2026-09-16: a LOCKED box carrying a person's name.
 * It belongs to the preparer, so it names no signer, and the lookup
 * that asks "whose box is this?" came back empty and printed nothing —
 * on his Mortgage form, and on 31 name boxes the Realty atlas had just
 * put on production.
 *
 * Such a box names its person by SEAT instead, and the casting says who
 * holds each seat on this send. So the question is asked in one place,
 * for both shapes, and every caller gets the same answer:
 *
 *   an ordinary box  → the signer it belongs to
 *   a locked box     → whoever is cast into its seat
 *   anything else    → null, which is reported missing and blocks the
 *                      send rather than borrowing somebody else's name
 *
 * `signerKeyByRole` is the casting. An empty map is the honest answer
 * for a send that cast no seats, and it yields null for every locked
 * box — the behaviour before seats existed.
 */
export function bindingPersonSignerKey(
  field: BindableField,
  signerKeyByRole: ReadonlyMap<string, string> = new Map(),
): string | null {
  if (field.signerKey && field.signerKey !== PREPARER_SIGNER_KEY) return field.signerKey;
  if (field.signerKey !== PREPARER_SIGNER_KEY) return null;
  const roleKey = field.roleKey?.trim();
  if (!roleKey) return null;
  return signerKeyByRole.get(roleKey) ?? null;
}

/** The signers who own at least one field that still needs a value from
 *  THEIR OWN contact record — i.e. who the caller must build a context
 *  for. Lives here so the override rule ("a typed value is not
 *  resolved") is applied in one place and cannot drift from the
 *  resolver below.
 *
 *  Takes the casting so a locked box bound to a person's field counts
 *  its seat's occupant — see bindingPersonSignerKey. */
export function contactBoundSignerKeys(
  fields: readonly BindableField[],
  signerKeyByRole: ReadonlyMap<string, string> = new Map(),
): Set<string> {
  const out = new Set<string>();
  for (const f of fields) {
    const token = (f.mergeToken ?? "").trim();
    if (!token || hasOverride(f)) continue;
    if (!isContactScopedToken(token)) continue;
    const key = bindingPersonSignerKey(f, signerKeyByRole);
    if (key) out.add(key);
  }
  return out;
}

/**
 * The seat a LOCKED box should start on the moment it is bound to a
 * person's field — before anyone has touched "Whose?" (Baker,
 * 2026-09-18: he placed one expecting it to print the first signer, and
 * it printed nothing instead, then blocked the send).
 *
 * A locked box belongs to nobody who signs, so a token that reads a
 * PERSON has nowhere to look unless a seat is chosen — see
 * bindingPersonSignerKey. Leaving it on "Nobody yet" is honest but
 * unhelpful: the box was just placed to print a name, and the form's
 * first signing seat (Borrower, Buyer 1, Named Insured, Grantor) is
 * the right guess far more often than not — the same reasoning
 * PrepareAndSend already uses for a new choose-one's Yes/No default.
 *
 * True only when THIS token, on THIS box, actually needs a guess:
 *   - not a locked box at all → nothing to default, the box has its
 *     own signer already.
 *   - a seat is already chosen → never overwritten. That covers both
 *     an atlas-placed box (its seat is recorded already) and a seat
 *     someone deliberately picked or cleared.
 *   - the token doesn't read a person → shared answers (a loan number,
 *     a property address) print the same in every box; there is no
 *     "whose" to guess.
 */
export function wantsDefaultSeat(
  token: string,
  isLockedBox: boolean,
  existingRoleKey: string | null | undefined,
): boolean {
  return isLockedBox && !existingRoleKey && isPersonScopedToken(token);
}

/**
 * Fill in every bound field from the record.
 *
 * A field with an OVERRIDE keeps it — that is the editability rule: a
 * merged value the advisor typed over stays typed over for this send,
 * and the binding survives so the next send resolves normally.
 */
export function resolveFieldBindings<T extends BindableField>(
  fields: T[],
  context: MergeContext,
): ResolveBindingsResult<T> {
  // Every field against the one context — the shape every caller but
  // the send path wants, and the shape the send path itself had until
  // per-signer contact values became a thing.
  return resolveFieldBindingsPerSigner(fields, context, () => context);
}

/**
 * The same resolution, but a field bound to a `contact.*` token reads
 * the record of the signer whose box it is.
 *
 * `contactContextFor` returns that signer's context, or null when the
 * signer has no contact record to read — a guest (a co-op agent, a
 * title officer, a seller) deliberately has none. Null does NOT fall
 * back to the shared context: falling back is precisely the bug being
 * fixed, and printing a client's name in a title officer's box is the
 * worst version of it, because it looks entirely deliberate. It is
 * reported as missing instead, which blocks the send and names the box.
 */
export function resolveFieldBindingsPerSigner<T extends BindableField>(
  fields: T[],
  /** For tokens that mean the same thing in every box. */
  sharedContext: MergeContext,
  contactContextFor: (field: T) => MergeContext | null,
  /** The name on the send of the person whose box this is, for a
   *  `signer.*` token (see isSignerScopedToken). Null means nobody is in
   *  that box on this send, which is reported missing like any other
   *  hole. Defaults to "nobody", so a caller that never passes it can
   *  only ever refuse a signer token, never print a wrong name. */
  signerNameFor: (field: T) => string | null = () => null,
): ResolveBindingsResult<T> {
  const resolved: ResolvedBinding[] = [];
  const missing: ResolveBindingsResult<T>["missing"] = [];
  const followedEnd = sixMonthsAfterTypedStart(fields);

  const out = fields.map((f, index) => {
    const token = (f.mergeToken ?? "").trim();
    if (!token || hasOverride(f)) return f;
    if (token === SEND_DATE_PLUS_6_TOKEN && followedEnd) {
      resolved.push({ index, token, value: followedEnd });
      return { ...f, value: followedEnd };
    }

    // Whose record this token reads. A shared token reads the record
    // the document is about; a contact token reads the person whose box
    // this is, and has nothing to read at all when that person is a
    // guest.
    let context: MergeContext | null;
    if (isSignerScopedToken(token)) {
      // The name on the send, and nothing else: deliberately a context
      // holding ONLY the signer namespace, so this box can never pick up
      // a record's or another person's value.
      const name = (signerNameFor(f) ?? "").trim();
      context = name ? { signer: { name } } : null;
    } else {
      context = isContactScopedToken(token) ? contactContextFor(f) : sharedContext;
    }
    if (!context) {
      missing.push({ index, token, pageNumber: f.pageNumber });
      return f;
    }

    // Resolved through the shared resolver rather than by reading the
    // context directly, so a document and an email fill the SAME token
    // the same way.
    //
    // TWO shapes mean "no value", and both must be caught:
    //   * empty — the token's whole namespace is absent;
    //   * the LITERAL {{token}} — an unknown token, or a known one whose
    //     leaf is empty on this record. The resolver leaves it as typed
    //     on purpose, precisely so a guard like this one notices; the
    //     email path's own unresolved-merge-fields guard reads the same
    //     signal.
    //
    // Missing the second case would print "{{service_record.amount}}"
    // onto a client's contract, which is worse than refusing to send.
    const raw = applyMergeFields(`{{${token}}}`, context).trim();
    const value = raw.includes("{{") ? "" : raw;
    if (!value) {
      missing.push({ index, token, pageNumber: f.pageNumber });
      return f;
    }
    resolved.push({ index, token, value });
    return { ...f, value };
  });

  return { fields: out, resolved, missing };
}

/**
 * The same question `resolveFieldBindings` answers at send time — which
 * bound fields will print nothing — asked BEFORE the click, against the
 * values the Prepare & Send screen already has in hand.
 *
 * It lives here, beside the send-time rule rather than in the component,
 * for one reason: the two must agree. A screen that says "all set" and a
 * server that then refuses the send is worse than no pre-flight at all,
 * and that drift is invisible until someone hits it on a real contract.
 * Same override rule, same two "no value" shapes.
 *
 * The caller supplies the lookup because the browser holds an already-
 * resolved token → value map from the merge-fields endpoint, not the
 * MergeContext the send path builds.
 */
export function unfilledBindings<T extends BindableField>(
  fields: readonly T[],
  /** The FIELD is handed over too, so a `contact.*` token can be looked
   *  up against the signer whose box it is — matching the send, which
   *  resolves it per signer. A caller that doesn't care may ignore it. */
  valueForToken: (token: string, field: T) => string | null | undefined,
): { index: number; token: string; pageNumber: number }[] {
  const out: { index: number; token: string; pageNumber: number }[] = [];
  fields.forEach((f, index) => {
    const token = (f.mergeToken ?? "").trim();
    if (!token || hasOverride(f)) return;
    const raw = (valueForToken(token, f) ?? "").trim();
    // Both shapes the send path treats as empty, so a screen can never
    // pass something the server will reject.
    if (!raw || raw.includes("{{")) {
      out.push({ index, token, pageNumber: f.pageNumber });
    }
  });
  return out;
}

/**
 * What the advisor is told when a binding has no value.
 *
 * Names the fields rather than reporting a count: "2 fields are missing"
 * sends someone hunting through a nine-page contract, and the whole
 * point of stopping the send is that they can fix it in a few seconds.
 */
export function missingBindingsMessage(
  missing: { token: string; pageNumber: number }[],
  labelForToken: (token: string) => string = (t) => t,
): string {
  if (missing.length === 0) return "";
  const named = missing
    .map((m) => `${labelForToken(m.token)} (page ${m.pageNumber})`)
    .join(", ");
  return missing.length === 1
    ? `This record has no value for ${named}. Fill it in on the record, type a value into the field, or remove the field — a document shouldn't go out with a blank there.`
    : `This record has no value for these fields: ${named}. Fill them in on the record, type values into the fields, or remove them — a document shouldn't go out with blanks.`;
}
